import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/KeycloakContext';
import CreditInsightReport from '../credits/CreditInsightReport';
import ApiUsage from './ApiUsage';
import Pagination from '../../ui/Pagination';
import OrganizationManagement from './OrganizationManagement';
import Banner from '../../ui/Banner';
import '../workspace/Members.css';
import '../credits/CreditInsightReport.css';

const SuperadminCreditUsage = () => {
  const { keycloak } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'insights');

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  // Date Helper for Insights
  const getPresetDates = (preset) => {
    const now = new Date();
    let start = new Date();
    let end = new Date();
    const formatLocalPart = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    switch (preset) {
      case 'Today':
        start = new Date(now);
        end = new Date(now);
        break;
      case 'This Week': {
        const day = now.getDay(); // Sunday=0, Monday=1 ...
        const diffToMonday = day === 0 ? 6 : day - 1;
        start = new Date(now);
        start.setDate(now.getDate() - diffToMonday);
        end = new Date(start);
        end.setDate(start.getDate() + 4);
        break;
      }
      case 'This Month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'This Year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return null;
    }

    return {
      startDate: formatLocalPart(start),
      endDate: formatLocalPart(end),
    };
  };

  const [insightDate, setInsightDate] = useState(getPresetDates('Today'));
  const [activePreset, setActivePreset] = useState('Today');
  const [insightGroup, setInsightGroup] = useState('day');

  const applyInsightPreset = (p) => {
    const dates = getPresetDates(p);
    if (dates) {
      setInsightDate(dates);
      setActivePreset(p);
      // Auto-set grouping based on range
      if (p === 'This Year') setInsightGroup('month');
      else setInsightGroup('day');
    }
  };

  const getTransactionLabel = (type) => {
    if (type === 'USAGE' || type === 'ENRICHMENT') return 'ENRICHMENT';
    if (type === 'B2C_RECOMMENDATION') return 'SEARCH PERSONA';
    if (type === 'MARKET_ANALYSIS') return 'MARKET ANALYSIS';
    if (type === 'SEARCH') return 'SEARCH';
    return String(type || '').replace(/_/g, ' ');
  };

  // Reset to Today when switching between tabs
  useEffect(() => {
    if (activeTab === 'insights') {
      applyInsightPreset('Today');
    }
  }, [activeTab]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, total: 0, limit: 10 });
  const [pageInput, setPageInput] = useState('1');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    transactionType: '',
    provider: '',
  });
  const [sort, setSort] = useState({
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });

  const handleSort = (field) => {
    const isAsc = sort.sortBy === field && sort.sortOrder === 'ASC';
    setSort({
      sortBy: field,
      sortOrder: isAsc ? 'DESC' : 'ASC',
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const getSortIcon = (field) => {
    if (sort.sortBy !== field)
      return (
        <i
          className="fas fa-sort"
          style={{ marginLeft: '4px', opacity: 0.3, fontSize: '10px' }}
        ></i>
      );
    return sort.sortOrder === 'ASC' ? (
      <i
        className="fas fa-sort-up"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    ) : (
      <i
        className="fas fa-sort-down"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    );
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${keycloak.token}` } };
      const { startDate, endDate, transactionType, provider } = filters;
      const { sortBy, sortOrder } = sort;

      if (activeTab === 'usage') {
        const queryParams = new URLSearchParams({
          page: pagination.page,
          limit: pagination.limit,
          sortBy,
          sortOrder,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(transactionType && { transactionType }),
          ...(provider && { provider }),
        }).toString();

        const res = await axios.get(`/credits/admin/usage?${queryParams}`, config);
        setUsage(res.data.data);
        setPagination((prev) => ({ ...prev, total: res.data.total }));
      }
    } catch (err) {
      console.error('[SuperadminCreditUsage] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, keycloak.token, pagination.page, pagination.limit, filters, sort]);

  useEffect(() => {
    if (activeTab !== 'insights') {
      // Reset page when filters change
      setPagination((prev) => ({ ...prev, page: 0 }));
      fetchData();
    }
  }, [activeTab, filters, sort, pagination.limit]);

  useEffect(() => {
    if (activeTab !== 'insights') {
      fetchData();
    }
  }, [pagination.page]);

  useEffect(() => {
    setPageInput(String(pagination.page + 1));
  }, [pagination.page]);

  return (
    <div className="tm-page">
      {/* Page Header (Banner) */}
      <Banner
        title="Credit Management"
        subtitle="Monitor platform-wide credit usage, track API interactions, and analyze usage trends."
        type="page-header"
      />

      {/* Tabs Navigation */}
      <div className="tm-section-tabs">
        <button
          className={`tm-section-tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          <i className="fas fa-chart-line"></i> Platform Insights
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'organizations' ? 'active' : ''}`}
          onClick={() => setActiveTab('organizations')}
        >
          <i className="fas fa-sitemap"></i> Organizations
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          <i className="fas fa-list-ul"></i> Usage Activity
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => setActiveTab('api')}
        >
          <i className="fas fa-microchip"></i> API Usage
        </button>
      </div>
      <div className="tm-horizontal-divider"></div>

      {activeTab === 'insights' && (
        <div
          className="tm-filter-bar animate-fade-in"
          style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}
        >
          <div
            className="tm-tabs"
            style={{
              display: 'flex',
              gap: '4px',
              background: '#f1f5f9',
              padding: '4px',
              borderRadius: '8px',
            }}
          >
            {['Today', 'This Week', 'This Month', 'This Year'].map((p) => (
              <button
                key={p}
                className={`tm-tab-pill ${activePreset === p ? 'active' : ''}`}
                onClick={() => applyInsightPreset(p)}
              >
                {p}
              </button>
            ))}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ marginRight: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>VIEW BY</span>
              <select
                className="tm-select sm"
                value={insightGroup || 'day'}
                onChange={(e) => setInsightGroup(e.target.value)}
                style={{
                  height: '36px',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '0 12px',
                  borderRadius: '8px',
                }}
              >
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
            <div className="tm-search" style={{ height: '36px', minWidth: '150px' }}>
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={insightDate.startDate}
                onChange={(e) => {
                  setInsightDate((prev) => ({ ...prev, startDate: e.target.value }));
                  setActivePreset('Custom');
                }}
              />
            </div>
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>TO</span>
            <div className="tm-search" style={{ height: '36px', minWidth: '150px' }}>
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={insightDate.endDate}
                onChange={(e) => {
                  setInsightDate((prev) => ({ ...prev, endDate: e.target.value }));
                  setActivePreset('Custom');
                }}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'insights' && (
        <div className="animate-fade-in">
          <CreditInsightReport isSuperadmin={true} dateRange={insightDate} groupBy={insightGroup} />
        </div>
      )}

      {activeTab === 'usage' && (
        <div className="animate-fade-in">
          {(() => {
            const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit));
            const goToPage = () => {
              const parsed = Number(pageInput);
              if (!Number.isFinite(parsed)) {
                setPageInput(String(pagination.page + 1));
                return;
              }
              const targetPage = Math.min(totalPages, Math.max(1, Math.floor(parsed)));
              setPagination((p) => ({ ...p, page: targetPage - 1 }));
              setPageInput(String(targetPage));
            };

            return (
              <>
                <div className="tm-toolbar">
                  <div className="tm-toolbar-group">
                    <label className="tm-toolbar-label">Action</label>
                    <select
                      className="tm-toolbar-select"
                      value={filters.transactionType}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, transactionType: e.target.value }))
                      }
                    >
                      <option value="">All types</option>
                      <option value="SEARCH">Search</option>
                      <option value="ENRICHMENT">Enrichment</option>
                      <option value="B2C_RECOMMENDATION">Search Persona</option>
                      <option value="MARKET_ANALYSIS">Market Analysis</option>
                    </select>
                  </div>
                  <div className="tm-toolbar-group">
                    <label className="tm-toolbar-label">Provider</label>
                    <select
                      className="tm-toolbar-select"
                      value={filters.provider}
                      onChange={(e) => setFilters((f) => ({ ...f, provider: e.target.value }))}
                    >
                      <option value="">All providers</option>
                      <option value="LUSHA">Lusha</option>
                      <option value="TAVILY">Tavily</option>
                      <option value="SYSTEM">System</option>
                    </select>
                  </div>
                  <div className="tm-toolbar-group">
                    <label className="tm-toolbar-label">From</label>
                    <input
                      className="tm-toolbar-input"
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="tm-toolbar-group">
                    <label className="tm-toolbar-label">To</label>
                    <input
                      className="tm-toolbar-input"
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                    />
                  </div>
                  <div
                    className="tm-toolbar-group tm-toolbar-actions"
                    style={{ minWidth: '118px', flex: '0 0 118px' }}
                  >
                    <label className="tm-toolbar-label" style={{ opacity: 0 }}>
                      &nbsp;
                    </label>
                    <button
                      className="tm-btn tm-btn-outline"
                      style={{ width: '100%', justifyContent: 'center', height: '32px' }}
                      onClick={() => {
                        setFilters({
                          startDate: '',
                          endDate: '',
                          transactionType: '',
                          provider: '',
                        });
                        setSort({ sortBy: 'created_at', sortOrder: 'DESC' });
                      }}
                    >
                      <i className="fas fa-times"></i> Clear
                    </button>
                  </div>
                </div>

                {/* Usage Table */}
                <Pagination
                  columns={[
                    { key: 'created_at', label: 'Timestamp', sortable: true, width: '150px' },
                    { key: 'user_id', label: 'User', sortable: true, width: '180px' },
                    { key: 'org_id', label: 'Org ID', width: '140px' },
                    { key: 'transaction_type', label: 'Type', width: '120px' },
                    { key: 'amount', label: 'Amount', sortable: true, width: '90px' },
                    { key: 'provider', label: 'Provider', width: '100px' },
                    { key: 'previous_balance', label: 'Before', width: '90px' },
                    { key: 'new_balance', label: 'After', width: '90px' },
                  ]}
                  data={usage}
                  onSort={handleSort}
                  sortBy={sort.sortBy}
                  sortOrder={sort.sortOrder}
                  renderRow={(u) => (
                    <tr key={u.id}>
                      <td style={{ fontSize: '11px', color: '#64748b' }}>
                        {new Date(u.created_at).toLocaleString()}
                      </td>
                      <td>
                        <div className="tm-member-cell">
                          <div
                            className="tm-avatar"
                            style={{
                              background: '#2563eb',
                              width: '24px',
                              height: '24px',
                              fontSize: '9px',
                            }}
                          >
                            {u.display_name?.substring(0, 2).toUpperCase() || '??'}
                          </div>
                          <span className="tm-name" style={{ fontSize: '12.5px' }}>
                            {u.display_name}
                          </span>
                        </div>
                      </td>
                      <td>
                        <code style={{ fontSize: '11px', color: '#64748b' }}>
                          {u.org_id || '-'}
                        </code>
                      </td>
                      <td>
                        <span
                          className={`tm-role-pill tm-role-organization_user`}
                          style={{ fontSize: '10px', textTransform: 'uppercase' }}
                        >
                          {getTransactionLabel(u.transaction_type)}
                        </span>
                      </td>
                      <td
                        style={{ color: u.amount < 0 ? '#ef4444' : '#10b981', fontWeight: '700' }}
                      >
                        {u.amount > 0 ? '+' : ''}
                        {u.amount.toLocaleString()}
                      </td>
                      <td>
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                          {u.provider}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: '#94a3b8' }}>
                        {(u.previous_balance || 0).toLocaleString()}
                      </td>
                      <td style={{ fontSize: '12px', color: '#94a3b8' }}>
                        {(u.new_balance || 0).toLocaleString()}
                      </td>
                    </tr>
                  )}
                  loading={loading}
                  currentPage={pagination.page + 1}
                  totalPages={Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit))}
                  onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p - 1 }))}
                  itemsPerPage={pagination.limit}
                  onItemsPerPageChange={(limit) =>
                    setPagination((prev) => ({ ...prev, page: 0, limit }))
                  }
                  totalItems={pagination.total || 0}
                  itemsName="records"
                  className="tm-usage-table-card"
                />
              </>
            );
          })()}
        </div>
      )}

      {activeTab === 'api' && (
        <div className="animate-fade-in">
          <ApiUsage />
        </div>
      )}

      {activeTab === 'organizations' && (
        <div className="animate-fade-in">
          <OrganizationManagement />
        </div>
      )}
    </div>
  );
};

export default SuperadminCreditUsage;
